import { deepmerge } from 'deepmerge-ts'
import { default as dot } from 'dot-object'
import { validator as joi } from '@nhtio/validation'
import { ResourcefulRouterOperation } from '../types'
import { E_INVALID_RESOURCEFUL_ROUTER_OPTIONS } from '@nhtio/lucid-resourceful/errors'
import { SchemaOrReferenceObjectSchema as schemaOrReferenceObjectSchema } from '../utils/openapi_validation_schemas'
import {
  ResourcefulPolicySchema,
  ResourcefulRouterMutatorOptionsSchema,
} from './resourceful_router_options_service'
import type { ValidationError } from '@nhtio/lucid-resourceful/joi'
import type {
  ResourcefulRouterModelServiceOptions,
  RouteCallback,
  AdditionalResourcefulRoutesMap,
  AdditionalResourcefulRouteDetails,
} from '../types'

const DEFAULT_RESOURCEFUL_ROUTER_MODEL_OPTIONS: ResourcefulRouterModelServiceOptions = {
  middleware: [],
  except: [],
  additional: {},
  headers: {},
  scopeRestrictors: [],
  payloadRestrictors: [],
  policies: [],
  mutators: {},
}

const FORBIDDEN_ADDITIONALS = [
  'get|/',
  'get|/$meta.index',
  'post|/',
  'get|/$meta.create',
  'get|/:id',
  'get|/:id/$meta.read',
  'get|/:id/:relationship',
  'get|/:id/:relationship/$meta.index',
  'put|/:id',
  'patch|/:id',
  'get|/$meta.update',
  'put|/$bulk',
  'put|/$bulk/:ids',
  'patch|/$bulk',
  'patch|/$bulk/:ids',
  'get|/$meta.$bulk.update',
  'put|/:id/:relationship',
  'patch|/:id/:relationship',
  'get|/:id/:relationship/$meta.sync',
  'delete|/:id',
  'get|/$meta.delete',
]

const RouteCallbackSchema = joi.function<RouteCallback>().required()

const AdditionalResourcefulRouteDetailsSchema = joi.object<AdditionalResourcefulRouteDetails>({
  title: joi.string().optional(),
  description: joi.string().optional(),
  handler: RouteCallbackSchema,
  requestPayloadSchema: schemaOrReferenceObjectSchema.optional(),
  responsePayloadSchema: schemaOrReferenceObjectSchema.optional(),
  tags: joi.alternatives(joi.string(), joi.array().items(joi.string())).optional(),
})

const AdditionalResourcefulRoutesMapValueSchema = joi.alternatives<
  RouteCallback | AdditionalResourcefulRouteDetails
>(RouteCallbackSchema, AdditionalResourcefulRouteDetailsSchema)

const ResourcefulRouterModelServiceOptionsMiddlewareSchema = joi.alternatives(
  joi.function(),
  joi
    .object({
      name: joi.string().required(),
      args: joi.array().optional(),
    })
    .unknown(true)
)

const ResourcefulRouterModelServiceOptionsSchema = joi.object<ResourcefulRouterModelServiceOptions>(
  {
    middleware: joi
      .alternatives(
        ResourcefulRouterModelServiceOptionsMiddlewareSchema,
        joi.array().items(ResourcefulRouterModelServiceOptionsMiddlewareSchema)
      )
      .required(),
    except: joi
      .array()
      .items(joi.string().valid(...ResourcefulRouterOperation))
      .required(),
    additional: joi
      .object<AdditionalResourcefulRoutesMap>()
      .pattern(
        joi
          .alternatives(joi.string(), joi.string().disallow(...FORBIDDEN_ADDITIONALS))
          .match('all'),
        AdditionalResourcefulRoutesMapValueSchema
      )
      .keys(
        Object.assign({}, ...FORBIDDEN_ADDITIONALS.map((route) => ({ [route]: joi.forbidden() })))
      )
      .custom((value, helpers) => {
        const keys = Object.keys(value)
        const passed = keys.every((key) => /^(get|post|put|patch|delete)\|\/.*$/gm.test(key))
        if (!passed) {
          return helpers.error('any.invalid')
        }
        return value
      }),
    headers: joi.object().pattern(joi.string(), joi.string()).default({}),
    scopeRestrictors: joi
      .array()
      .items(
        joi.alternatives(
          joi.function().arity(0),
          joi.function().arity(1),
          joi.function().arity(2),
          joi.function().arity(3)
        )
      )
      .optional()
      .default([]),
    payloadRestrictors: joi
      .array()
      .items(
        joi.alternatives(joi.function().arity(0), joi.function().arity(1), joi.function().arity(2))
      )
      .optional()
      .default([]),
    policies: joi.array().items(ResourcefulPolicySchema).optional().default([]),
    mutators: ResourcefulRouterMutatorOptionsSchema.optional().default({}),
  }
)

export class ResourcefulRouterModelServiceOptionsService {
  readonly #options: ResourcefulRouterModelServiceOptions

  private constructor(options: ResourcefulRouterModelServiceOptions) {
    this.#options = Object.freeze(deepmerge({}, options))
    Object.defineProperty(this, 'middleware', {
      get: () => this.#options.middleware,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'except', {
      get: () => this.#options.except,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'additional', {
      get: () => this.#options.additional,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'headers', {
      get: () => this.#options.headers,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'scopeRestrictors', {
      get: () => this.#options.scopeRestrictors,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'payloadRestrictors', {
      get: () => this.#options.payloadRestrictors,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'policies', {
      get: () => this.#options.policies,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'mutators', {
      get: () => this.#options.mutators,
      enumerable: true,
      configurable: false,
    })
  }

  declare middleware: Readonly<ResourcefulRouterModelServiceOptions['middleware']>
  declare except: Readonly<ResourcefulRouterModelServiceOptions['except']>
  declare additional: Readonly<ResourcefulRouterModelServiceOptions['additional']>
  declare headers: Readonly<ResourcefulRouterModelServiceOptions['headers']>
  declare scopeRestrictors: Readonly<ResourcefulRouterModelServiceOptions['scopeRestrictors']>
  declare payloadRestrictors: Readonly<ResourcefulRouterModelServiceOptions['payloadRestrictors']>
  declare policies: Readonly<ResourcefulRouterModelServiceOptions['policies']>
  declare mutators: Readonly<ResourcefulRouterModelServiceOptions['mutators']>

  get(key: string): any {
    return dot.pick(key, this.#options, false)
  }

  public static init(options: unknown): ResourcefulRouterModelServiceOptionsService {
    // step 1: at minimum, ensure that we are dealing with an object
    const { error: checkIfObjectError } = joi.object().unknown(true).required().validate(options, {
      abortEarly: true,
    })
    if (checkIfObjectError) {
      throw new E_INVALID_RESOURCEFUL_ROUTER_OPTIONS(checkIfObjectError)
    }
    // step 2: create an object which is merged with the defaults
    const mergedOptions = deepmerge(DEFAULT_RESOURCEFUL_ROUTER_MODEL_OPTIONS, options)
    // step 3: validate the merged options
    const {
      error: ResourcefulRouterModelServiceOptionsValidationError,
      value: ResourcefulRouterModelServiceOptions,
    } = ResourcefulRouterModelServiceOptionsSchema.validate(mergedOptions, {
      abortEarly: false,
      convert: true,
      allowUnknown: true,
      stripUnknown: true,
    }) as { error?: ValidationError; value: ResourcefulRouterModelServiceOptions }
    if (ResourcefulRouterModelServiceOptionsValidationError) {
      throw new E_INVALID_RESOURCEFUL_ROUTER_OPTIONS(
        ResourcefulRouterModelServiceOptionsValidationError
      )
    }
    const instance = new ResourcefulRouterModelServiceOptionsService(
      ResourcefulRouterModelServiceOptions
    )
    return instance
  }
}
